import { useQuery } from '@tanstack/react-query';

import { expectStatus } from '@/lib/api/client';
import { listTaskAttachments } from '@/lib/api/generated/api';
import { taskKeys } from '../queryKeys';

/**
 * The files in a task's attachments folder (spec 120 FR-006, FR-009).
 *
 * Read once when the drawer shows the task and never polled: attachments
 * change when a person adds or removes one, and those actions invalidate this
 * key themselves. A file dropped into the folder from the Files page appears
 * on the next open.
 */
export function useTaskAttachments(
  workspaceId: string,
  taskId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const query = useQuery({
    queryKey: taskKeys.attachments(workspaceId, taskId),
    queryFn: async () =>
      expectStatus(
        await listTaskAttachments(workspaceId, taskId),
        200,
        'Attachments could not be loaded.',
      ),
    enabled,
    retry: false,
  });
  return {
    attachments: query.data?.attachments ?? [],
    isPending: query.isPending,
    loadFailed: query.isError,
    retry: () => void query.refetch(),
  };
}
